import type { ErrorEvent, EventHint } from '@sentry/types';

const NEVER_LOG_KEYS = new Set([
  'phone_number',
  'phone_e164',
  'recovery_phrase',
  'seed',
  'private_key',
  'signing_key',
  'key_package',
  'welcome',
  'commit',
  'mls_secret',
  'epoch_secret',
  'application_secret',
  'init_secret',
  'psk',
  'password',
  'pin',
  'otp',
  'sms_code',
  'verification_code',
  'auth_code',
  'refresh_token',
  'access_token',
  'id_token',
  'device_secret',
  'push_token',
  'apns_token',
  'fcm_token',
  'livekit_token',
  'authorization',
  'cookie',
]);

const SENSITIVE_PATTERN = /secret|token|password|key|seed|hash|cipher|nonce/i;

function shouldRedact(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return NEVER_LOG_KEYS.has(lowerKey) || SENSITIVE_PATTERN.test(key);
}

function redactValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((item) => redactValue(item));
    }

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (shouldRedact(key)) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = redactValue(val);
      }
    }
    return result;
  }

  return value;
}

export function beforeSend(
  event: ErrorEvent,
  _hint: EventHint,
): ErrorEvent | PromiseLike<ErrorEvent | null> | null {
  try {
    if (event.extra) {
      event.extra = redactValue(event.extra) as typeof event.extra;
    }

    if (event.contexts) {
      event.contexts = redactValue(event.contexts) as typeof event.contexts;
    }

    if (event.request) {
      event.request = redactValue(event.request) as typeof event.request;
    }

    if (event.user) {
      event.user = redactValue(event.user) as typeof event.user;
    }

    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
        if (!breadcrumb.data) {
          return breadcrumb;
        }
        return {
          ...breadcrumb,
          data: redactValue(breadcrumb.data) as Record<string, unknown>,
        };
      });
    }

    return event;
  } catch (error) {
    return null;
  }
}
